import React, { useState, useRef, useEffect } from "react";
import { AppLink, Modal } from "../../shared";
import {
  FaCalendarAlt,
  FaWarehouse,
  FaSync,
  FaCogs,
  FaBuilding,
  FaUser,
  FaIndustry,
  FaPowerOff,
  FaInfoCircle,
  FaCoffee,
  FaMobileAlt,
  FaMicrochip,
  FaEllipsisV,
  FaChevronDown,
  FaArrowRight,
  FaExternalLinkAlt,
  FaTint,
  FaTimes,
  FaCircle,
  FaSlidersH,
  FaRandom,
  FaSitemap,
  FaUndo,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import { chartColors } from "../../../Config/Colors";
import { RiScales2Fill } from "react-icons/ri";
import { ThreeDots } from "react-loader-spinner";
import { formatDate, daysInStatus, isStaleStatus } from "./utils";
import { getMainBoardVersionText } from "../../../utils/deviceCards";
import {
  deviceActivityStateLabel,
  deviceActivityToggleLabel,
  displayLogEntry,
  displayLogText,
} from "../../../utils/deviceActivityLabels";
import usePermission from "../../../Hooks/usePermission";
import useAuth from "../../../Hooks/useAuth";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";
import {
  getDeviceDisplayName,
  getDeviceCupNumber,
  getGrinderStatus as getGrinderStatusFromUtils,
  getDeviceComponentsByKey,
  isWaterModeApplicable as isWaterModeApplicableUtil,
  shouldShowDevicePowerState,
  canToggleDeviceActivity,
  getDealerMoveActions,
  getCenterDealerMoveActions,
  WATER_MODE_COMPONENT_KEYS,
} from "../../../utils/deviceUtils";
import { useGetDeviceLastOperationMutation } from "../../../store/storageRoles/devicesSharedApi";
import coffeeBean from "../../../Assets/icons/coffee_bean.png";
import teaLeaf from "../../../Assets/icons/tea_leaf.png";
import turkishCoffeeMachine from "../../../Assets/icons/turkish-coffee-machine.png";
import teaFilterCoffeeMachine from "../../../Assets/icons/tea-filter-coffee-machine-icon.png";
import teaTurkishCoffeeMachine from "../../../Assets/icons/tea_coffee_machine.png";
import espressoMachine from "../../../Assets/icons/espresso_machine.png";
import logger from "../../../utils/logger";

const DEVICE_IMAGES = {
  Fincanda: turkishCoffeeMachine,
  Çayset: teaFilterCoffeeMachine,
  "Çayset-Fincanda": teaTurkishCoffeeMachine,
  Espresso: espressoMachine,
};

const PRODUCT_ICONS = {
  Çay: teaLeaf,
  "Türk Kahvesi": coffeeBean,
  "Filtre Kahve": coffeeBean,
  Espresso: coffeeBean,
};

function DeviceCard({
  device,
  token,
  onDeviceClick,
  onMoveToStorage,
  onMoveToProduction,
  onResetQuota,
  onDistributeQuota,
  isFirmQuotaBusy = false,
  isMoveToStorageLoading,
  isResetQuotaLoading,
  isMoveToProductionLoading,
  setOperationHandler,
  setSelectedOperationDevice,
  onOpenOperationDrawer,
  // Merkezden bayi adına taşıma (#329) — (device, "toCustomer" | "takeBack")
  onOpenCenterDealerMove,
  // Bayi cihazına Kota Ver / Kota Al (#335) — (device, "give" | "take")
  onOpenCenterDealerQuota,
  isMobileView = false,
  openActionsDeviceId,
  setOpenActionsDeviceId,
  isDealer = false,
  hasSubDealers = false,
}) {
  const { hasPermission, hasAnyPermission } = usePermission();
  const { auth } = useAuth();
  const hasAnyDeviceTabPermission = hasAnyPermission(
    "devices.info", "devices.location", "devices.settings", "devices.maintenance", "devices.stats", "devices.reports"
  );
  const hasAnyFirmTabPermission = hasAnyPermission(
    "firms.info", "firms.devices", "firms.stats", "firms.bills",
    "firms.productSettings", "firms.belowFirms", "firms.users",
    "firms.customers", "firms.employees"
  );
  // Bayide YOK (#180): eski şablonda devices.lastOperation kalmış olabilir.
  const hasLastOperation = !isDealer && hasPermission("devices.lastOperation");
  // Versiyon rozetleri cihaz detayındaki versiyon kartıyla aynı yetkiye bağlı.
  const canSeeVersions = hasPermission("devices.infoVersion");

  // Aksiyon durumu (openActionsDeviceId) masaüstü satır ve mobil kart
  // örnekleri arasında paylaşılır. Mobil kartın aksiyon modalı Modal ile
  // body'ye portallandığı için, kart CSS ile gizli olsa da (ListView md:hidden)
  // masaüstünde de görünür. Bu yüzden modalı canlı viewport ölçüsüyle
  // (ListView'in md=768px kırılımı) sınırlıyoruz — geniş ekranda yalnızca
  // dropdown çıksın.
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsNarrowViewport(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const showActions = openActionsDeviceId === device?._id;
  const setShowActions = (val) => {
    if (val) {
      setOpenActionsDeviceId?.(device?._id);
    } else {
      setOpenActionsDeviceId?.(null);
    }
  };
  // Masaüstü aksiyon menüsü: alttaki satırlarda menü taşmasın diye yukarı açılır.
  const actionsBtnRef = useRef(null);
  const [dropUp, setDropUp] = useState(false);
  const [showOperationDetails, setShowOperationDetails] = useState(false);
  const [operationDetails, setOperationDetails] = useState(null);
  const [isLoadingOperationDetails, setIsLoadingOperationDetails] =
    useState(false);
  const [firmCellHovered, setFirmCellHovered] = useState(false);

  // API hook'u
  // Son İşlem yalnız depoda — bayi görmez (#180).
  const [getDeviceLastOperation] = useGetDeviceLastOperationMutation();

  // "Son İşlemi Gör" paneli de bir drawer — diğerleriyle aynı giriş/çıkış
  // kayması. (Önceden sabit translate-x-0 ile basılıyordu: hem açılırken hem
  // kapanırken animasyonsuzdu.)
  const {
    mounted: opDetailsMounted,
    entered: opDetailsEntered,
  } = useDrawerTransition(showOperationDetails);

  if (!device) return null;

  const deviceName = getDeviceDisplayName(device);
  // Bayi panelinde bayinin kendi firmasındaki cihazlar (depo/servis/plasiyer)
  // sahada olmadığı için Açık/Kapalı rozeti hiç basılmaz.
  const showPowerState = shouldShowDevicePowerState(auth, device);
  const serialNo = device.serialNo || "Seri No Yok";
  const lastOperation = device.lastInfoOperation || {};
  const operationManager = lastOperation.operationManager || "Belirtilmemiş";
  const description = displayLogText(lastOperation.description) || "Açıklama yok";
  const valueFrom = lastOperation.valueFrom || "";
  const valueTo = lastOperation.valueTo || "";
  const note = displayLogText(lastOperation.note) || "";
  const createdInfo = lastOperation.createdInfo
    ? formatDate(lastOperation.createdInfo)
    : "Belirtilmemiş";
  const locationIconMap = {
    Depo: FaWarehouse,
    Firma: FaBuilding,
    Servis: FaCogs,
    Plasiyer: FaUser,
  };
  const locationColorMap = {
    Depo: "bg-purple-50 text-purple-700 border-purple-200",
    Firma: "bg-indigo-50 text-indigo-700 border-indigo-200",
    Servis: "bg-teal-50 text-teal-700 border-teal-200",
    Plasiyer: "bg-blue-50 text-blue-700 border-blue-200",
  };
  const getLocationBadge = (value) => {
    const Icon = locationIconMap[value];
    const color =
      locationColorMap[value] || "bg-gray-50 text-gray-700 border-gray-200";
    return (
      <span
        className={`inline-flex w-fit items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${color}`}
      >
        {Icon && <Icon className="w-3 h-3" />}
        {value || "-"}
      </span>
    );
  };

  const statusIconMap = {
    Depo: FaWarehouse,
    Firma: FaBuilding,
    Servis: FaCogs,
    Plasiyer: FaUser,
    Üretim: FaIndustry,
  };
  const displayStatusName = isDealer ? device.dealerDeviceStatusName : device.deviceStatusName;
  const StatusIcon = statusIconMap[displayStatusName];
  // Durum rozetinde konum bulunma hâli ekiyle okunur (Depo → Depoda).
  // Eşleşme yoksa ham ad gösterilir (yeni/bilinmeyen durum adları bozulmasın).
  const STATUS_LOCATIVE = {
    Depo: "Depoda",
    Firma: "Firmada",
    Servis: "Serviste",
    Plasiyer: "Plasiyerde",
    Üretim: "Üretimde",
  };
  const statusDisplayLabel = STATUS_LOCATIVE[displayStatusName] || displayStatusName;
  // Uzun süredir Servis/Plasiyer'de → satır/kart kırmızı yanıp söner (#80).
  // Bayi kendi durumunu (dealerDeviceStatusName) kendi damgasıyla görür.
  const statusSince = isDealer ? device.dealerStatusChangedAt : device.statusChangedAt;
  const isStale = isStaleStatus(displayStatusName, statusSince);
  const staleTitle = isStale
    ? `${daysInStatus(statusSince)} gündür ${statusDisplayLabel.toLocaleLowerCase("tr-TR")}`
    : undefined;
  // Plasiyerdeki cihazlarda rozette plasiyerin adı da gösterilir.
  const playMakerFullName = device.playMaker
    ? `${device.playMaker.name || ""}${device.playMaker.lastName ? ` ${device.playMaker.lastName}` : ""}`.trim()
    : "";
  const showPlayMakerName = displayStatusName === "Plasiyer" && Boolean(playMakerFullName);
  const isConnected = device.deviceInfo?.connection?.status;

  const productQuotas = Array.isArray(device.productInfo?.productInfo)
    ? device.productInfo.productInfo
    : [];
  const visibleQuotas = productQuotas;
  const getComponentsByKey = (key) => getDeviceComponentsByKey(device, key);

  const getComponentBadgeStyle = (variant) => {
    switch (variant) {
      case "success":
        return "bg-green-50 text-green-700 border-green-100";
      case "grinder":
        return "bg-amber-50 text-amber-700 border-amber-100";
      case "cup":
        return "bg-slate-50 text-slate-700 border-slate-200";
      case "danger":
        return "bg-red-50 text-red-700 border-red-100";
      case "info":
        return "bg-blue-50 text-blue-700 border-blue-100";
      default:
        return "bg-gray-50 text-gray-600 border-gray-100";
    }
  };

  const cupNumber = getDeviceCupNumber(device);
  const firmDisplayName = (() => {
    const name = device.firm?.firmName || "Belirtilmemiş";
    return name.charAt(0).toUpperCase() + name.slice(1);
  })();

  const getStatusVariantFromLabel = (label) => {
    switch (label) {
      case "Aktif":
        return "success";
      case "Arızalı":
        return "danger";
      case "Şebeke":
      case "Damacana":
      case "Şebeke + Damacana":
        return "info";
      case "Pasif":
      default:
        return "muted";
    }
  };

  const grinderStatusFromUtils = getGrinderStatusFromUtils(device);
  const getGrinderStatus = () => ({
    ...grinderStatusFromUtils,
    state:
      grinderStatusFromUtils.label === "-"
        ? "unknown"
        : grinderStatusFromUtils.label === "Aktif"
          ? "active"
          : grinderStatusFromUtils.label === "Arızalı"
            ? "fault"
            : "passive",
  });

  const deviceNameLabel = deviceName;
  const isWaterModeApplicable = isWaterModeApplicableUtil(deviceNameLabel);

  const getWaterModeStatus = () => {
    if (!isWaterModeApplicable) {
      return { label: "-", variant: "muted" };
    }
    if (device.componentSummary?.waterMode) {
      const wm = device.componentSummary.waterMode;
      // Deaktif/Pasif su modu → "-" (gizlenir); değirmen mantığıyla aynı.
      if (wm === "-" || wm === "Pasif") {
        return { label: "-", variant: "muted" };
      }
      return { label: wm, variant: getStatusVariantFromLabel(wm) };
    }
    // Backend eski makineleri (mainsWaterPump/carboyWaterPump) waterTank* key'lerine normalize ediyor; tek set kullanıyoruz
    const mainsComponents = getComponentsByKey(WATER_MODE_COMPONENT_KEYS.mains);
    const carboyComponents = getComponentsByKey(WATER_MODE_COMPONENT_KEYS.carboy);
    const mainsActive = mainsComponents.some((component) => component?.active);
    const carboyActive = carboyComponents.some((component) => component?.active);
    const anyFault =
      mainsComponents.some((component) => component?.fault) ||
      carboyComponents.some((component) => component?.fault);

    if (mainsActive && carboyActive) {
      return { label: "Şebeke + Damacana", variant: "info" };
    }
    if (mainsActive) {
      return { label: "Şebeke", variant: "info" };
    }
    if (carboyActive) {
      return { label: "Damacana", variant: "info" };
    }
    if (anyFault) {
      return { label: "Arızalı", variant: "danger" };
    }
    // Aktif/arızalı su pompası yok → "-" (deaktif = yok), değirmen gibi.
    return { label: "-", variant: "muted" };
  };

  const grinderStatus = getGrinderStatus();
  const waterModeStatus = getWaterModeStatus();
  const hasGrinderInfo = Boolean(
    grinderStatus.label && grinderStatus.label !== "-"
  );
  const hasWaterModeInfo =
    isWaterModeApplicable &&
    waterModeStatus.label &&
    waterModeStatus.label !== "-"
      ? true
      : false;

  // Bu cihaz için herhangi bir işlem devam ediyor mu?
  // isFirmQuotaBusy: cihazın firmasında kota dağıtımı sürüyor — tüm aksiyonlar meşgul.
  const isAnyOperationInProgress =
    isMoveToStorageLoading || isResetQuotaLoading || isFirmQuotaBusy;

  // İşlem detaylarını getir
  const handleGetOperationDetails = async () => {
    setIsLoadingOperationDetails(true);
    try {
      const response = await getDeviceLastOperation({
        token: token,
        deviceId: device._id,
        deviceStatusName: device.deviceStatusName,
      }).unwrap();

      setOperationDetails(displayLogEntry(response.data.lastInfoOperation));
      setShowOperationDetails(true);
    } catch (error) {
      logger.error("İşlem detayları alınırken hata:", error);
      // Hata durumunda mevcut veriyi kullan
      setOperationDetails(displayLogEntry(lastOperation));
      setShowOperationDetails(true);
    } finally {
      setIsLoadingOperationDetails(false);
    }
  };

  // Cihaz durumuna göre renk belirleme (soluk arka plan, buton görünümü yok)
  const getStatusColor = () => {
    switch (displayStatusName) {
      case "Servis":
        return "bg-teal-50 text-teal-700 border border-teal-100";
      case "Firma":
        return "bg-indigo-50 text-indigo-700 border border-indigo-100";
      case "Depo":
        return "bg-purple-50 text-purple-700 border border-purple-100";
      case "Plasiyer":
        return "bg-blue-50 text-blue-700 border border-blue-100";
      case "Üretim":
        return "bg-green-50 text-green-700 border border-green-100";
      default:
        return "bg-gray-50 text-gray-600 border border-gray-100";
    }
  };

  // Aksiyon butonları
  const getActionButtons = () => {
    const actions = [];
    const statusName = isDealer ? device.dealerDeviceStatusName : device.deviceStatusName;

    // Depoya Al butonu — Firma dahil (Depo hariç) tüm konumlardan (storage).
    // Bayide taşıma aksiyonları getDealerMoveActions'tan gelir (#247, aşağıda).
    if (!isDealer && hasPermission("devices.moveToStorage") && statusName !== "Depo") {
      actions.push({
        label: "Depoya Al",
        icon: <FaWarehouse className="w-4 h-4" />,
        color: "bg-purple-50 text-purple-700 hover:bg-purple-100",
        onClick: () => {
          if (typeof onOpenOperationDrawer === "function") {
            onOpenOperationDrawer(device, "Depo");
            return;
          }
          if (typeof onMoveToStorage === "function") {
            onMoveToStorage(device);
            return;
          }
          setSelectedOperationDevice(device);
          setOperationHandler("Depo");
        },
        loading: isMoveToStorageLoading,
      });
    }

    // Kota Almaya Aç/Kapa butonu (isActive)
    // Bayi panelinde YALNIZCA müşteri firmasındaki cihazda gösterilir
    // (kullanıcı kararı 2026-08-17): bayinin elindeki cihaz — depo, servis,
    // plasiyer fark etmeksizin — her taşımada kapalı alınır ve kapalı kalır,
    // sunucu da bu uçta 400 döner. Bu karar, bayi deposunda aç/kapat
    // serbestliği veren 2026-08-13 kararının yerini aldı.
    // Depo (storage) tarafında eski kural sürüyor: depoda gizli.
    if (
      hasPermission("devices.toggleActivity") &&
      canToggleDeviceActivity(auth, device) &&
      (isDealer || statusName !== "Depo")
    ) {
      actions.push({
        label: deviceActivityToggleLabel(device.isActive),
        icon: <FaPowerOff className="w-4 h-4" />,
        color: device.isActive
          ? "bg-red-50 text-red-700 hover:bg-red-100"
          : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
        onClick: () => {
          if (typeof onOpenOperationDrawer === "function") {
            onOpenOperationDrawer(device, device.isActive ? "AktiflikKapat" : "AktiflikAc");
            return;
          }
          setSelectedOperationDevice(device);
          setOperationHandler("Aktiflik");
        },
      });
    }

    // Üretime Al butonu (sadece storage)
    if (!isDealer && hasPermission("devices.moveToStorage") && device.firm?.storage && statusName === "Depo") {
      actions.push({
        label: "Üretime Al",
        icon: <FaBuilding className="w-4 h-4" />,
        color: "bg-gray-50 text-gray-700 hover:bg-gray-100",
        onClick: () => {
          if (typeof onOpenOperationDrawer === "function") {
            onOpenOperationDrawer(device, "Üretim");
            return;
          }
          if (typeof onMoveToProduction === "function") {
            onMoveToProduction(device);
          }
        },
        loading: isMoveToProductionLoading,
      });
    }

    // Kotasını Yenile butonu — BAYİDE YOK (kullanıcı kararı 2026-08-13).
    // İşlev yalnızca depo tarafına ait: cihazın fiziksel kotasını sıfırlayıp
    // depo firmasına iade eder. Sunucuda da bayiye kapalı — endpoint yalnızca
    // /api/devices/storageRoles/resetOperationQuota altında ve
    // restrictTo("admin","storage") ile korunuyor; dealer-roles karşılığı yok.
    // Buton bayide görünüyordu ve tıklanınca storage route'una gidip 403 alıyordu.
    if (
      !isDealer &&
      hasPermission("devices.quota") &&
      statusName !== "Firma" &&
      statusName !== "Depo"
    ) {
      actions.push({
        label: "Kotasını Yenile",
        icon: <FaSync className="w-4 h-4" />,
        color: "bg-green-50 text-green-700 hover:bg-green-100",
        onClick: () => onResetQuota(device),
        loading: isResetQuotaLoading,
      });
    }

    // Manuel Kota Yükle butonu — firma sayfasındaki drawer ile aynı koşullar.
    // Bayide YOK (#178): eski şablonlarda devices.quota kalmış olabilir.
    if (
      !isDealer &&
      hasPermission("devices.quota") &&
      statusName === "Firma" &&
      device?.productInfo?.productInfo?.length > 0 &&
      !device?.serialNo?.startsWith("X460PLUS")
    ) {
      actions.push({
        label: "Manuel Kota Yükle",
        icon: <RiScales2Fill className="w-4 h-4" />,
        color: "bg-amber-50 text-amber-700 hover:bg-amber-100",
        onClick: () => {
          setSelectedOperationDevice(device);
          setOperationHandler("ManuelKota");
        },
      });
    }

    // Kota Dağıt — cihazın firmasının dağıtılmamış kotasını firmanın cihazlarına
    // dağıtır (firma cihazlar sayfasındaki düğmeyle aynı). Yalnız depo paneli.
    // Bayideki cihaz her zaman kapalı — orada Kota Ver / Kota Al (#335, aşağıda).
    const isDealerHeldForCenter = !isDealer && device.firm?.firmType === "dealer";
    if (
      onDistributeQuota &&
      !isDealer &&
      !isDealerHeldForCenter &&
      hasPermission("firms.distributeQuota") &&
      statusName === "Firma" &&
      device.firm?._id
    ) {
      actions.push({
        label: "Kota Dağıt",
        icon: <FaRandom className="w-4 h-4" />,
        color: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
        onClick: () => onDistributeQuota(device),
        loading: isFirmQuotaBusy,
      });
    }

    // Bayi cihazına Kota Ver / Kota Al (#335): canlı, bayi bakiyesinden
    if (
      onOpenCenterDealerQuota &&
      isDealerHeldForCenter &&
      hasPermission("firms.distributeQuota")
    ) {
      actions.push({
        label: "Kota Ver",
        icon: <FaPlus className="w-4 h-4" />,
        color: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
        onClick: () => onOpenCenterDealerQuota(device, "give"),
      });
      actions.push({
        label: "Kota Al",
        icon: <FaMinus className="w-4 h-4" />,
        color: "bg-amber-50 text-amber-700 hover:bg-amber-100",
        onClick: () => onOpenCenterDealerQuota(device, "take"),
      });
    }

    // Plasiyere Ver butonu
    if (hasPermission("devices.moveToPlaymaker") && statusName !== "Üretim") {
      actions.push({
        label: "Plasiyere Ver",
        icon: <FaUser className="w-4 h-4" />,
        color: "bg-blue-50 text-blue-700 hover:bg-blue-100",
        onClick: () => {
          if (typeof onOpenOperationDrawer === "function") {
            onOpenOperationDrawer(device, "Plasiyer");
            return;
          }
          setSelectedOperationDevice(device);
          setOperationHandler("Plasiyer");
        },
      });
    }

    // Servise Al — storage: Depo/Firma/Üretim/Plasiyer (sunucu beyaz listesiyle aynı,
    // device-status.controller moveDeviceToService). Bayi: aşağıda (#247).
    if (
      !isDealer &&
      hasPermission("devices.moveToService") &&
      statusName !== "Servis" &&
      ["Depo", "Firma", "Üretim", "Plasiyer"].includes(statusName)
    ) {
      actions.push({
        label: "Servise Al",
        icon: <FaCogs className="w-4 h-4" />,
        color: "bg-teal-50 text-teal-700 hover:bg-teal-100",
        onClick: () => {
          if (typeof onOpenOperationDrawer === "function") {
            onOpenOperationDrawer(device, "Servis");
            return;
          }
          setSelectedOperationDevice(device);
          setOperationHandler("Servis");
        },
      });
    }

    // Firmaya Al butonu (storage)
    if (!isDealer && hasPermission("devices.moveToFirm") && ["Depo", "Plasiyer", "Servis"].includes(statusName)) {
      actions.push({
        label: "Firmaya Al",
        icon: <FaBuilding className="w-4 h-4" />,
        color: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
        onClick: () => {
          if (typeof onOpenOperationDrawer === "function") {
            onOpenOperationDrawer(device, "Firma");
            return;
          }
          setSelectedOperationDevice(device);
          setOperationHandler("Firma");
        },
      });
    }

    // Merkezden bayi adına taşıma (#329): bayideki cihaz → Bayi Müşterisine
    // Ver; bayiye bağlı müşterideki cihaz → Bayiye Geri Al.
    if (typeof onOpenCenterDealerMove === "function") {
      const CENTER_DEALER_MOVE_BUTTONS = {
        toCustomer: {
          permission: "devices.moveToFirm",
          label: "Bayi Müşterisine Ver",
          icon: <FaBuilding className="w-4 h-4" />,
          color: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
        },
        takeBack: {
          permission: "devices.moveToStorage",
          label: "Bayiye Geri Al",
          icon: <FaUndo className="w-4 h-4" />,
          color: "bg-purple-50 text-purple-700 hover:bg-purple-100",
        },
      };
      getCenterDealerMoveActions(auth, device).forEach((key) => {
        const btn = CENTER_DEALER_MOVE_BUTTONS[key];
        if (!hasPermission(btn.permission)) return;
        actions.push({
          label: btn.label,
          icon: btn.icon,
          color: btn.color,
          onClick: () => onOpenCenterDealerMove(device, key),
        });
      });
    }

    // Bayi taşımaları (#247): Alt Firmaya / Alt Bayiye Ver, Bayiye Geri Al,
    // Depoya Al, Servise Al (merkez servisi). Bayi yalnız drawer akışını kullanır.
    if (isDealer && typeof onOpenOperationDrawer === "function") {
      const DEALER_MOVE_BUTTONS = {
        toSubFirm: {
          permission: "devices.moveToFirm",
          label: "Alt Firmaya Ver",
          icon: <FaBuilding className="w-4 h-4" />,
          color: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
          open: () => onOpenOperationDrawer(device, "Firma", { firmTarget: "customer" }),
        },
        toSubDealer: {
          permission: "devices.moveToFirm",
          label: "Alt Bayiye Ver",
          icon: <FaSitemap className="w-4 h-4" />,
          color: "bg-blue-50 text-blue-700 hover:bg-blue-100",
          open: () => onOpenOperationDrawer(device, "Firma", { firmTarget: "dealer" }),
        },
        takeBack: {
          permission: "devices.moveToStorage",
          label: "Bayiye Geri Al",
          icon: <FaUndo className="w-4 h-4" />,
          color: "bg-purple-50 text-purple-700 hover:bg-purple-100",
          open: () => onOpenOperationDrawer(device, "Depo"),
        },
        toStorage: {
          permission: "devices.moveToStorage",
          label: "Depoya Al",
          icon: <FaWarehouse className="w-4 h-4" />,
          color: "bg-purple-50 text-purple-700 hover:bg-purple-100",
          open: () => onOpenOperationDrawer(device, "Depo"),
        },
        toService: {
          permission: "devices.moveToService",
          label: "Servise Al",
          icon: <FaCogs className="w-4 h-4" />,
          color: "bg-teal-50 text-teal-700 hover:bg-teal-100",
          open: () => onOpenOperationDrawer(device, "Servis"),
        },
      };
      getDealerMoveActions(auth, device, { hasSubDealers }).forEach((key) => {
        const btn = DEALER_MOVE_BUTTONS[key];
        if (!hasPermission(btn.permission)) return;
        actions.push({
          label: btn.label,
          icon: btn.icon,
          color: btn.color,
          onClick: btn.open,
          loading: key === "takeBack" || key === "toStorage" ? isMoveToStorageLoading : undefined,
        });
      });
    }

    return actions;
  };

  // Mobile Card View
  if (isMobileView) {
    return (
      <>
        {/* İşlem Detayları Modal - Mobile */}
        {opDetailsMounted && (
          <div className="fixed inset-0 z-50">
            <div
              className={`absolute inset-0 bg-black bg-opacity-40 transition-opacity duration-300 ease-in-out ${opDetailsEntered ? "opacity-100" : "opacity-0"}`}
              onClick={() => setShowOperationDetails(false)}
            />
            <div
              className={`absolute right-0 top-0 h-screen w-full bg-white shadow-2xl border-l border-gray-200 overflow-y-auto flex flex-col transform ${opDetailsEntered ? "translate-x-0" : "translate-x-full"} transition-transform duration-300 ease-in-out`}
            >
              {/* Header */}
              <div className="bg-indigo-600 text-white px-4 py-3.5 shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="bg-white/10 rounded-lg p-2">
                      <FaInfoCircle className="text-sm" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold">Son İşlem Detayları</h3>
                      <div className="text-xs text-indigo-100">{deviceName} • {serialNo}</div>
                    </div>
                  </div>
                  <button onClick={() => setShowOperationDetails(false)} className="text-white/90 hover:text-white p-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-3.5 space-y-3 flex-1 overflow-y-auto">
                {/* Cihaz Bilgileri */}
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-3">
                  <div className="font-medium text-gray-900 mb-2.5 flex items-center gap-2 text-sm">
                    <FaInfoCircle className="text-indigo-500 shrink-0" />
                    <span>Cihaz Bilgileri</span>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Cihaz Adı</span>
                      <span className="font-medium text-gray-900 text-right">{deviceName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Seri No</span>
                      <span className="font-medium text-gray-900">{serialNo}</span>
                    </div>
                  </div>
                </div>

                {/* İşlem Bilgileri */}
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-3">
                  <div className="font-medium text-gray-900 mb-2.5 flex items-center gap-2 text-sm">
                    <FaCalendarAlt className="text-blue-500 shrink-0" />
                    <span>İşlem Bilgileri</span>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">İşlemi Yapan</span>
                      <span className="font-medium text-gray-900 text-right">{operationDetails?.operationManager || operationManager}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">İşlem Tarihi</span>
                      <span className="font-medium text-gray-900 text-right">{operationDetails?.createdInfo ? formatDate(operationDetails.createdInfo) : createdInfo}</span>
                    </div>
                  </div>
                </div>

                {/* Konum Değişimi */}
                {(operationDetails?.valueFrom || operationDetails?.valueTo || valueFrom || valueTo) && (
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-3">
                    <div className="font-medium text-gray-900 mb-2.5 flex items-center gap-2 text-sm">
                      <FaWarehouse className="text-purple-500 shrink-0" />
                      <span>Konum Değişimi</span>
                    </div>
                    <div className="flex items-center gap-2 justify-center">
                      <div className="flex-1 flex flex-col items-center p-2.5 rounded-lg bg-white border border-gray-100">
                        <span className="text-xs font-medium text-gray-400 mb-1.5">Önce</span>
                        {getLocationBadge(operationDetails?.valueFrom || valueFrom || "-")}
                      </div>
                      <div className="text-gray-300 shrink-0">
                        <FaArrowRight className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 flex flex-col items-center p-2.5 rounded-lg bg-white border border-gray-100">
                        <span className="text-xs font-medium text-gray-400 mb-1.5">Sonra</span>
                        {getLocationBadge(operationDetails?.valueTo || valueTo || "-")}
                      </div>
                    </div>
                  </div>
                )}

                {/* Not */}
                {(operationDetails?.note || note) && (
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-3">
                    <div className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm">
                      <FaInfoCircle className="text-amber-500 shrink-0" />
                      <span>Not</span>
                    </div>
                    <div className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {operationDetails?.note || note}
                    </div>
                  </div>
                )}

                {/* İşlem Açıklaması */}
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-3">
                  <div className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm">
                    <FaCogs className="text-teal-500 shrink-0" />
                    <span>İşlem Açıklaması</span>
                  </div>
                  <div className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {(operationDetails?.description || description) &&
                      (operationDetails?.description || description) !== "Açıklama yok" ? (
                      (operationDetails?.description || description)
                        .split("\n")
                        .map((line, index) => (
                          <div key={index} className="mb-1">
                            {line.includes(":") ? (
                              <>
                                <span className="font-semibold text-gray-900">{line.split(":")[0]}:</span>
                                <span className="ml-1">{line.split(":").slice(1).join(":").trim()}</span>
                              </>
                            ) : (
                              line
                            )}
                          </div>
                        ))
                    ) : (
                      <span className="text-gray-400 italic">Açıklama bulunmuyor</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Card — navigasyon soldaki Git kutusunda; kartın kendisi tıklanmaz (firma linki rahat tıklansın) */}
        <div
          className={`${isStale ? "animate-staleBlink" : "bg-white"} px-3.5 py-2.5 border-b border-gray-200`}
          title={staleTitle}
        >
          {/* Üst: Açık/Kapalı + Git butonu, Seri No + Model ile bütünleşik (sol) / Konum (sağ).
              Açık/Kapalı gizlendiğinde (bayinin kendi firmasındaki cihaz) kutu
              nötr renkte yalnız "Git" olarak kalır; yetki de yoksa hiç basılmaz. */}
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {hasAnyDeviceTabPermission ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onDeviceClick && onDeviceClick(device); }}
                  className={`flex flex-col items-center justify-center shrink-0 rounded-lg px-2 py-1 transition-opacity active:opacity-80 ${!showPowerState ? "bg-slate-700" : device.isActive ? "bg-green-700" : "bg-red-700"}`}
                >
                  {showPowerState && (
                    <span className="max-w-[64px] text-center text-[10px] font-Bold text-white leading-tight">
                      {deviceActivityStateLabel(device.isActive)}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-white/80 whitespace-nowrap">
                    <FaExternalLinkAlt className="w-2 h-2 shrink-0" />
                    Git
                  </span>
                </button>
              ) : showPowerState ? (
                <div className={`flex flex-col items-center justify-center shrink-0 rounded-lg px-2 py-1 ${device.isActive ? "bg-green-700" : "bg-red-700"}`}>
                  <span className="max-w-[64px] text-center text-[10px] font-Bold text-white leading-tight">
                    {deviceActivityStateLabel(device.isActive)}
                  </span>
                </div>
              ) : null}
              <div className="min-w-0">
                <div className="text-sm font-bold text-gray-900 truncate">{serialNo}</div>
                <div className="text-xs font-medium text-gray-600 truncate" title={device.customName ? `${device.customName} (${deviceName})` : deviceName}>
                  {device.customName ? (
                    <>
                      {device.customName}
                      <span className="ml-1 text-gray-400 font-normal">({deviceName})</span>
                    </>
                  ) : (
                    deviceName
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0 max-w-[48%]">
              <span className={`inline-flex w-fit px-2 py-0.5 rounded-md text-xs font-semibold ${getStatusColor()} ${showPlayMakerName ? "flex-col items-end gap-0" : "items-center gap-1"}`}>
                <span className="inline-flex items-center gap-1">
                  {StatusIcon && <StatusIcon className="w-2.5 h-2.5 shrink-0" />}
                  {statusDisplayLabel}
                </span>
                {showPlayMakerName && (
                  <span className="text-[10px] font-medium">{playMakerFullName}</span>
                )}
              </span>
              {isStale && (
                <span className="text-[10px] font-SemiBold text-red-700">{staleTitle}</span>
              )}
              {/* Firma — durumun altında */}
              {device.firm?._id && hasAnyFirmTabPermission ? (
                <AppLink
                  to={`/Anasayfa/Firma/${device.firm._id}`}
                  state={{ firm: device.firm, fromPage: "Cihaz" }}
                  className="inline-flex items-center gap-1 rounded-lg bg-indigo-50/70 border border-indigo-100/80 px-2 py-0.5 hover:bg-indigo-100/80 transition-colors max-w-full"
                >
                  <FaBuilding className="w-3 h-3 text-indigo-500 shrink-0" />
                  <span className="text-xs font-semibold text-gray-900 truncate">{firmDisplayName}</span>
                  <FaArrowRight className="w-2.5 h-2.5 text-indigo-500 shrink-0" />
                </AppLink>
              ) : (
                <div className="inline-flex items-center gap-1 rounded-lg bg-indigo-50/70 border border-indigo-100/80 px-2 py-0.5 max-w-full">
                  <FaBuilding className="w-3 h-3 text-indigo-500 shrink-0" />
                  <span className="text-xs font-semibold text-gray-900 truncate">{firmDisplayName}</span>
                </div>
              )}
            </div>
          </div>

          {/* Meta rozetleri: bağlantı + bileşenler tek satırda */}
          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <span className={`inline-flex w-fit items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-md border ${isConnected ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-gray-50 text-gray-500 border-gray-100"}`}>
              <span className="relative flex h-2 w-2 shrink-0">
                {isConnected && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>}
                <FaCircle className={`relative inline-flex h-2 w-2 ${isConnected ? "text-emerald-500" : "text-gray-300"}`} />
              </span>
              {isConnected ? "Bağlı" : "Bağlı Değil"}
              {!isConnected && device.deviceInfo?.connection?.lastConnected && (
                <span className="font-normal text-gray-400">
                  · {new Date(device.deviceInfo.connection.lastConnected).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
                </span>
              )}
            </span>
            <span className={`inline-flex w-fit items-center gap-1 text-xs px-1.5 py-0.5 rounded-md ${getComponentBadgeStyle("cup")}`}>
              <FaCoffee className="w-2.5 h-2.5 shrink-0" /> {cupNumber} göz
            </span>
            {hasGrinderInfo && (
              <span className={`inline-flex w-fit items-center gap-1 text-xs px-1.5 py-0.5 rounded-md ${getComponentBadgeStyle("grinder")}`}>
                <FaCogs className="w-2.5 h-2.5 shrink-0" /> Değirmen
              </span>
            )}
            {hasWaterModeInfo && (
              <span className={`inline-flex w-fit items-center gap-1 text-xs px-1.5 py-0.5 rounded-md ${getComponentBadgeStyle(waterModeStatus.variant)}`}>
                <FaTint className="w-2.5 h-2.5 shrink-0" /> {waterModeStatus.label}
              </span>
            )}
          </div>

          {/* Kotalar — inline chip (ürün: değer) */}
          {visibleQuotas.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {visibleQuotas.map((item, idx) => (
                <span key={idx} className="inline-flex items-center gap-1 text-xs bg-gray-50 border border-gray-100 rounded-md px-2 py-0.5 font-medium text-gray-700">
                  <img src={PRODUCT_ICONS[item.productName] || coffeeBean} alt="" className="w-3.5 h-3.5 object-contain shrink-0" />
                  {item.productName}: <span className="font-bold text-gray-900">{item.quota ?? 0}</span>
                </span>
              ))}
            </div>
          )}

          {/* Son İşlemler + Aksiyonlar */}
          {(hasLastOperation || getActionButtons().length > 0) && (
            <div className="flex items-center gap-2 pt-1.5 border-t border-gray-100">
              {hasLastOperation && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleGetOperationDetails(); }}
                  disabled={isLoadingOperationDetails || isAnyOperationInProgress}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                >
                  {isLoadingOperationDetails ? (
                    <ThreeDots height={12} width={16} color="#fff" />
                  ) : (
                    <FaCalendarAlt className="w-3 h-3" />
                  )}
                  <span>Son İşlemler</span>
                </button>
              )}
              {getActionButtons().length > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowActions(!showActions); }}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium transition-colors border border-gray-200"
                >
                  <FaEllipsisV className="w-3 h-3" />
                  <span>Aksiyonlar</span>
                </button>
              )}
            </div>
          )}

          {/* Aksiyonlar — mobilde inline panel yerine ortak modal (seri no başlıkta) */}
          <Modal
            open={showActions && isNarrowViewport && getActionButtons().length > 0}
            onClose={() => setShowActions(false)}
            maxWidth="sm"
            zIndex={9999}
          >
            <Modal.Header
              title="Aksiyonlar"
              subtitle={`Seri No: ${serialNo}`}
              onClose={() => setShowActions(false)}
            />
            <Modal.Body className="space-y-1.5">
              {getActionButtons().map((action, index) => (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); action.onClick(); setShowActions(false); }}
                  disabled={isAnyOperationInProgress || action.loading}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${action.color} ${isAnyOperationInProgress || action.loading ? "opacity-50" : ""}`}
                >
                  {action.loading ? <ThreeDots height={14} width={18} color="#6B7280" /> : action.icon}
                  <span>{action.label}</span>
                </button>
              ))}
            </Modal.Body>
          </Modal>
        </div>
      </>
    );
  }

  return (
    <>
      {/* İşlem Detayları Drawer */}
      {opDetailsMounted && (
        <>
          <div
            className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] transition-opacity duration-300 ease-in-out ${opDetailsEntered ? "opacity-100" : "opacity-0"}`}
            onClick={() => setShowOperationDetails(false)}
          />
          <div
            className={`fixed inset-y-0 right-0 w-full sm:w-[420px] transform ${opDetailsEntered ? "translate-x-0" : "translate-x-full"} transition-transform duration-300 ease-in-out shadow-2xl z-[9999] flex flex-col bg-white`}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <FaInfoCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold tracking-wide">Son İşlem Detayları</h3>
                    <p className="text-xs text-white/80 mt-0.5">
                      {deviceName} : {serialNo}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowOperationDetails(false)}
                  className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-5">
              <div className="space-y-4">
                {/* Cihaz Bilgileri */}
                <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                  <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-indigo-50 to-white border-b border-gray-100">
                    <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <FaCoffee className="w-3.5 h-3.5 text-indigo-600" />
                    </div>
                    <span className="text-sm font-semibold text-gray-800">Cihaz Bilgileri</span>
                  </div>
                  <div className="px-4 py-3 grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Cihaz Adı</p>
                      <p className="text-sm font-semibold text-gray-800 mt-0.5">{deviceName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Seri No</p>
                      <p className="text-sm font-semibold text-gray-800 mt-0.5">{serialNo}</p>
                    </div>
                  </div>
                </div>

                {/* İşlem Bilgileri */}
                <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                  <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-blue-50 to-white border-b border-gray-100">
                    <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                      <FaCalendarAlt className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <span className="text-sm font-semibold text-gray-800">İşlem Bilgileri</span>
                  </div>
                  <div className="px-4 py-3 grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">İşlemi Yapan</p>
                      <p className="text-sm font-semibold text-gray-800 mt-0.5">
                        {operationDetails?.operationManager || operationManager}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">İşlem Tarihi</p>
                      <p className="text-sm font-semibold text-gray-800 mt-0.5">
                        {operationDetails?.createdInfo
                          ? formatDate(operationDetails.createdInfo)
                          : createdInfo}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Konum Değişimi */}
                {(operationDetails?.valueFrom ||
                  operationDetails?.valueTo ||
                  valueFrom ||
                  valueTo) && (
                    <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                      <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-purple-50 to-white border-b border-gray-100">
                        <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                          <FaWarehouse className="w-3.5 h-3.5 text-purple-600" />
                        </div>
                        <span className="text-sm font-semibold text-gray-800">Konum Değişimi</span>
                      </div>
                      <div className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 p-3 rounded-xl bg-gray-50 border border-gray-100 text-center">
                            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1.5">Önce</p>
                            {getLocationBadge(operationDetails?.valueFrom || valueFrom || "-")}
                          </div>
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-50 shrink-0">
                            <FaArrowRight className="w-3 h-3 text-indigo-500" />
                          </div>
                          <div className="flex-1 p-3 rounded-xl bg-gray-50 border border-gray-100 text-center">
                            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1.5">Sonra</p>
                            {getLocationBadge(operationDetails?.valueTo || valueTo || "-")}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Not */}
                {(operationDetails?.note || note) && (
                  <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                    <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-amber-50 to-white border-b border-gray-100">
                      <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                        <FaInfoCircle className="w-3.5 h-3.5 text-amber-600" />
                      </div>
                      <span className="text-sm font-semibold text-gray-800">Not</span>
                    </div>
                    <div className="px-4 py-3">
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {operationDetails?.note || note}
                      </p>
                    </div>
                  </div>
                )}

                {/* İşlem Açıklaması */}
                <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                  <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-teal-50 to-white border-b border-gray-100">
                    <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center">
                      <FaCogs className="w-3.5 h-3.5 text-teal-600" />
                    </div>
                    <span className="text-sm font-semibold text-gray-800">İşlem Açıklaması</span>
                  </div>
                  <div className="px-4 py-3">
                    {(operationDetails?.description || description) &&
                      (operationDetails?.description || description) !==
                      "Açıklama yok" ? (
                      <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {(operationDetails?.description || description)
                          .split("\n")
                          .map((line, index) => (
                            <div key={index} className="mb-1">
                              {line.includes(":") ? (
                                <>
                                  <span className="font-semibold text-gray-900">
                                    {line.split(":")[0]}:
                                  </span>
                                  <span className="ml-1.5">
                                    {line.split(":").slice(1).join(":").trim()}
                                  </span>
                                </>
                              ) : (
                                line
                              )}
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        Açıklama bulunmuyor
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Tablo Satırı */}
      <tr
        className={`${isStale ? "animate-staleBlink" : `bg-white even:bg-gray-50/70 ${firmCellHovered ? "" : "hover:bg-gray-100/70"}`} transition-colors duration-200 border-b border-gray-100`}
        title={staleTitle}
      >
        {/* Cihaz Bilgileri — firma cihaz tablosu deseni: ikon + seri no +
            model tek link (group-hover'da üçü birden link görünümü);
            altında Açık/Kapalı + Bağlı rozetleri */}
        <td className="px-3 py-3 w-[16%]">
          <div className="flex flex-col gap-1.5 min-w-0">
            {(() => {
              const devImg = DEVICE_IMAGES[device.deviceInfo?.deviceName];
              const inner = (
                <>
                  <div className="w-11 h-11 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border border-transparent transition-colors group-hover:bg-indigo-50 group-hover:border-indigo-300">
                    {devImg ? (
                      <img src={devImg} alt={device.deviceInfo?.deviceName} className="max-w-full max-h-full object-contain p-0.5" />
                    ) : (
                      <FaMobileAlt className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <p className="text-sm font-bold text-gray-900 truncate m-0 transition-colors group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
                      {serialNo}
                    </p>
                    <p
                      className="text-xs text-gray-600 font-medium truncate m-0 transition-colors group-hover:text-indigo-500 group-hover:underline underline-offset-2 decoration-indigo-300"
                      title={device.customName ? `${device.customName} (${deviceName})` : deviceName}
                    >
                      {device.customName ? (
                        <>
                          {device.customName}
                          <span className="ml-1 text-gray-400 font-normal group-hover:text-indigo-400">({deviceName})</span>
                        </>
                      ) : (
                        deviceName
                      )}
                    </p>
                  </div>
                </>
              );
              return hasAnyDeviceTabPermission ? (
                <AppLink
                  to={`/Anasayfa/${isDealer ? "Cihazlarım" : "Cihazlar"}/${device._id}`}
                  className="group flex items-center gap-2.5 min-w-0 w-fit max-w-full"
                  title="Cihaza git"
                >
                  {inner}
                </AppLink>
              ) : (
                <div className="flex items-center gap-2.5 min-w-0">{inner}</div>
              );
            })()}
            {/* Açık/Kapalı + Bağlı rozetleri — firma tablosu stili.
                Açık/Kapalı bayinin kendi firmasındaki cihazlarda gizlenir. */}
            <div className="flex flex-wrap items-center gap-1.5">
              {showPowerState && (
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold whitespace-nowrap ${
                    device.isActive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${device.isActive ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
                  {deviceActivityStateLabel(device.isActive)}
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold whitespace-nowrap ${
                  isConnected ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                }`}
                title={
                  !isConnected && device.deviceInfo?.connection?.lastConnected
                    ? `Son bağlantı: ${new Date(device.deviceInfo.connection.lastConnected).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}`
                    : undefined
                }
              >
                <span className={`w-2 h-2 rounded-full ${isConnected ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
                {isConnected ? "Bağlı" : "Bağlı Değil"}
              </span>
            </div>
            {!isConnected && device.deviceInfo?.connection?.lastConnected && (
              <span className="text-[11px] text-gray-400">
                Son bağlantı: {new Date(device.deviceInfo.connection.lastConnected).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
              </span>
            )}
          </div>
        </td>

        {/* Bileşenler */}
        <td className="px-3 py-3 w-[14%]">
          <div className="flex flex-wrap gap-1.5">
            <span className={`inline-flex w-fit items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${getComponentBadgeStyle("cup")}`}>
              <FaCoffee className="w-3 h-3 shrink-0 opacity-75" />
              {cupNumber} göz
            </span>
            {hasGrinderInfo && (
              <span
                className={`inline-flex w-fit items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${getComponentBadgeStyle(
                  "grinder"
                )}`}
              >
                <FaCogs className="w-3 h-3 shrink-0 opacity-75" />
                Değirmen
              </span>
            )}
            {hasWaterModeInfo && (
              <span
                className={`inline-flex w-fit items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${getComponentBadgeStyle(
                  waterModeStatus.variant
                )}`}
              >
                <FaTint className="w-3 h-3 shrink-0 opacity-75" />
                {waterModeStatus.label}
              </span>
            )}
          </div>
        </td>

        {/* Durum — konum ifadesi bulunma hâli ekiyle (Depoda, Firmada...) */}
        <td className="px-3 py-3 w-[11%]">
          <div className="flex flex-col gap-1.5">
            {isAnyOperationInProgress ? (
              <span className="inline-flex w-fit items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-50 text-gray-600 border border-gray-100">
                <ThreeDots height={12} width={16} color="#6B7280" />
                İşlemde
              </span>
            ) : (
              <span
                className={`inline-flex w-fit gap-0.5 px-2.5 py-1 rounded-lg text-xs font-medium ${getStatusColor()} ${showPlayMakerName ? "flex-col items-start" : "items-center gap-1.5"}`}
              >
                <span className="inline-flex items-center gap-1.5">
                  {StatusIcon && <StatusIcon className="w-3 h-3 shrink-0 opacity-80" />}
                  {statusDisplayLabel}
                </span>
                {showPlayMakerName && (
                  <span className="text-[11px] font-semibold">{playMakerFullName}</span>
                )}
              </span>
            )}
            {isStale && !isAnyOperationInProgress && (
              <span className="text-[11px] font-SemiBold text-red-700">{staleTitle}</span>
            )}
          </div>
        </td>

        {/* Versiyonlar — firma cihaz tablosu deseni (Ekran/Kart rozetleri).
            devices.infoVersion yoksa sütun DevicesList'te de kaldırılıyor;
            iki koşul aynı kalmalı yoksa hücreler başlıklardan kayar. */}
        {canSeeVersions && (
        <td className="px-3 py-3 w-[10%] min-w-0">
          <div className="flex flex-col gap-1 w-fit max-w-full">
            <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 font-semibold whitespace-nowrap max-w-full">
              <FaMobileAlt className="w-3 h-3 shrink-0" />
              <span className="truncate" title={`Ekran: ${device.deviceInfo?.appVersion || "-"}`}>
                Ekran: {device.deviceInfo?.appVersion || "-"}
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 font-semibold whitespace-nowrap max-w-full">
              <FaMicrochip className="w-3 h-3 shrink-0" />
              <span className="truncate" title={`Kart: ${getMainBoardVersionText(device.deviceInfo) || "-"}`}>
                Kart: {getMainBoardVersionText(device.deviceInfo) || "-"}
              </span>
            </span>
          </div>
        </td>
        )}

        {/* Kotalar — firma cihaz tablosu deseni: ürün başına satır
            (ikon + isim + kota; nokta rengi kota dağıtım durumu) */}
        <td className="px-2 py-3 w-[13%] max-w-0">
          {productQuotas.length === 0 ? (
            <span className="text-xs text-gray-300">-</span>
          ) : (
            <div className="flex flex-col gap-1 overflow-hidden">
              {visibleQuotas.map((item, index) => (
                <span key={index} className="inline-flex items-center gap-2 text-sm whitespace-nowrap min-w-0">
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      item.quotaDistribution ? "bg-emerald-500" : "bg-gray-300"
                    }`}
                    title={item.quotaDistribution ? "Kota almaya açık" : "Kota almaya kapalı"}
                  />
                  <img
                    src={PRODUCT_ICONS[item.productName] || coffeeBean}
                    alt={item.productName || "Ürün"}
                    className="w-3.5 h-3.5 object-contain shrink-0"
                  />
                  <span className="text-gray-600 truncate">{item.productName || "-"}</span>
                  <span className="font-bold text-indigo-600 tabular-nums shrink-0">
                    {(item.quota ?? 0).toLocaleString("tr-TR")}
                  </span>
                </span>
              ))}
            </div>
          )}
        </td>

        {/* Firma */}
        <td
          className="px-3 py-3 w-[14%] min-w-0 align-middle"
          onMouseEnter={() => setFirmCellHovered(true)}
          onMouseLeave={() => setFirmCellHovered(false)}
        >
          {/* Solda firma ikonu; sağında iki satır: firma adı + soluk Bayser
              No. Üç parça (ikon, isim, bayser) tek link — herhangi birine
              hover'da hepsi birlikte link görünümüne geçer (group-hover).
              Eski "Git" kutusu kaldırıldı. */}
          {device.firm?._id && hasAnyFirmTabPermission ? (
            <AppLink
              to={`/Anasayfa/Firma/${device.firm._id}`}
              state={{ firm: device.firm, fromPage: "Cihaz" }}
              className="group flex items-center gap-2 min-w-0 w-fit max-w-full"
              title="Firmaya git"
            >
              <div className="flex items-center justify-center shrink-0 w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 transition-colors group-hover:bg-indigo-100 group-hover:border-indigo-300">
                <FaBuilding className="w-4 h-4 text-indigo-500 transition-colors group-hover:text-indigo-700" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-semibold text-gray-900 break-words leading-snug transition-colors group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
                  {firmDisplayName}
                </span>
                {device.firm?.bayserNo && (
                  <span className="text-[11px] font-medium text-gray-400 truncate transition-colors group-hover:text-indigo-500 group-hover:underline underline-offset-2 decoration-indigo-300">
                    Bayser: {device.firm.bayserNo}
                  </span>
                )}
              </div>
            </AppLink>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex items-center justify-center shrink-0 w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100">
                <FaBuilding className="w-4 h-4 text-indigo-500" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-semibold text-gray-900 break-words leading-snug min-w-0">
                  {firmDisplayName}
                </span>
                {device.firm?.bayserNo && (
                  <span className="text-[11px] font-medium text-gray-400 truncate">
                    Bayser: {device.firm.bayserNo}
                  </span>
                )}
              </div>
            </div>
          )}
        </td>

        {/* Aksiyonlar */}
        {(hasLastOperation || getActionButtons().length > 0) && (
        <td className="px-3 py-3 w-[12%] min-w-0">
          <div className="relative space-y-1.5">
            {hasLastOperation && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleGetOperationDetails();
              }}
              disabled={isLoadingOperationDetails || isAnyOperationInProgress}
              className="flex w-full items-center justify-between gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-indigo-50 text-indigo-600 border border-indigo-100 hover:bg-indigo-100 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="inline-flex items-center gap-1.5">
                {isLoadingOperationDetails || isAnyOperationInProgress ? (
                  <ThreeDots height={14} width={18} color={chartColors.bar} />
                ) : (
                  <FaCalendarAlt className="w-3.5 h-3.5 shrink-0" />
                )}
                Son İşlemi Gör
              </span>
            </button>
            )}
            {getActionButtons().length > 0 && (
            <>
            <button
              ref={actionsBtnRef}
              onClick={(e) => {
                e.stopPropagation();
                if (!showActions && actionsBtnRef.current) {
                  // Menü aşağı sığmıyorsa (viewport veya liste scroll alanı) yukarı aç.
                  const rect = actionsBtnRef.current.getBoundingClientRect();
                  const menuHeight = getActionButtons().length * 56 + 16;
                  const scroller = actionsBtnRef.current.closest(".overflow-y-auto");
                  const bottomLimit = scroller
                    ? Math.min(scroller.getBoundingClientRect().bottom, window.innerHeight)
                    : window.innerHeight;
                  setDropUp(rect.bottom + menuHeight > bottomLimit - 8);
                }
                setShowActions(!showActions);
              }}
              className="flex w-full items-center justify-between gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100 transition-colors whitespace-nowrap"
            >
              <span className="inline-flex items-center gap-1.5">
                {isAnyOperationInProgress ? (
                  <ThreeDots
                    visible={true}
                    height="16"
                    width="20"
                    color="#6B7280"
                    ariaLabel="loading-indicator"
                  />
                ) : (
                  <FaSlidersH className="w-3.5 h-3.5 shrink-0" />
                )}
                Aksiyonlar
              </span>
              <FaChevronDown
                className={`w-2.5 h-2.5 shrink-0 opacity-60 transition-transform duration-200 ${showActions ? "rotate-180" : ""
                  }`}
              />
            </button>

            {/* Animasyonlu Aksiyon Menüsü */}
            {showActions && (
              <div className={`absolute right-0 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden ${dropUp ? "bottom-full mb-2" : "top-full mt-2"}`}>
                <div className="p-2 space-y-1">
                  {getActionButtons().map((action, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        action.onClick();
                        setShowActions(false);
                      }}
                      disabled={isAnyOperationInProgress || action.loading}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 ${action.color
                        } ${isAnyOperationInProgress || action.loading
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                        }`}
                      style={{
                        animationDelay: `${index * 50}ms`,
                        animation: "slideInFromRight 0.3s ease-out forwards",
                      }}
                    >
                      {action.loading ? (
                        <ThreeDots
                          visible={true}
                          height="16"
                          width="20"
                          color="#6B7280"
                          ariaLabel="loading-indicator"
                        />
                      ) : (
                        action.icon
                      )}
                      <span>{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            </>
            )}
          </div>
        </td>
        )}
      </tr>
    </>
  );
}

export default DeviceCard;
